import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  EmptyState,
  FormField,
  StyledSelect,
} from '@jumpstone/ui-react';
import {
  default_selected_period,
  period_bounds,
  period_key,
  selectable_periods,
} from './pay_run_calculations';
import type {
  PayRunMutationFailure,
  PayRunMutationResult,
  BackPayWarning,
  SkippedForeignCurrencyEmployee,
} from '../../data/use_pay_runs';
import type { PayPeriod, PayRun } from '../../data/types';
import { format_date, format_month_year } from '../../lib/format';
import { restartSignIn } from '../../lib/auth/step_up.js';

/**
 * "No pay run is open" panel: the employer picks the payroll month, then
 * starts a draft pay run for exactly that month. Shown for a first-ever run,
 * after the newest run was voided, and on demand once the latest run is
 * finished (see `RunPayrollScreen`).
 */
export function StartPayRunPanel({
  pay_runs,
  start_pay_run,
  is_read_only_grant,
  on_started,
  back_to_period,
  on_back,
}: {
  pay_runs: readonly PayRun[];
  start_pay_run:
    | ((input: {
        period_start: string;
        period_end: string;
      }) => Promise<PayRunMutationResult>)
    | undefined;
  is_read_only_grant: boolean;
  /**
   * Called after a successful start with the employees left out for a
   * foreign pay currency and any back pay that could not be computed.
   */
  on_started: (
    skipped: readonly SkippedForeignCurrencyEmployee[],
    back_pay_warnings: readonly BackPayWarning[],
  ) => void;
  /** Period of the finished run the employer came from, for the back link. */
  back_to_period?: PayPeriod;
  on_back?: () => void;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const [is_starting, set_is_starting] = useState(false);
  // `YYYY-MM` key of the month the employer picked; `undefined` until they
  // touch the picker, so the default tracks freshly-loaded pay-run history.
  const [period_choice, set_period_choice] = useState<string | undefined>(
    undefined,
  );
  const [start_error, set_start_error] = useState<
    PayRunMutationFailure | undefined
  >(undefined);

  const period_options = selectable_periods(pay_runs);
  // A picked month that has since gained a run (refetch, or another admin
  // created it) falls back to the default instead of a guaranteed conflict.
  const picked = period_options.find(
    (option) =>
      !option.already_run && period_key(option.period) === period_choice,
  );
  const chosen_period =
    picked?.period ?? default_selected_period(period_options);
  const chosen_bounds = chosen_period
    ? period_bounds(chosen_period)
    : undefined;

  const back_link =
    back_to_period && on_back ? (
      <Button variant="ghost" size="sm" onClick={on_back}>
        {t('pay_run.back_to_run', {
          period: format_month_year(back_to_period, locale),
        })}
      </Button>
    ) : null;

  return (
    <EmptyState
      title={t('pay_run.none_open_title')}
      description={t('pay_run.none_open_description')}
      action={
        start_pay_run ? (
          <div className="flex flex-col items-center gap-2">
            <FormField
              label={t('pay_run.period_select_label')}
              htmlFor="pay-run-period"
              helpText={
                chosen_bounds
                  ? t('pay_run.period_select_hint', {
                      start: format_date(chosen_bounds.period_start, locale),
                      end: format_date(chosen_bounds.period_end, locale),
                    })
                  : t('pay_run.period_select_all_run')
              }
            >
              <StyledSelect
                id="pay-run-period"
                value={chosen_period ? period_key(chosen_period) : ''}
                placeholder={t('pay_run.period_select_placeholder')}
                disabled={is_starting}
                triggerClassName="min-w-[220px]"
                options={period_options.map((option) => ({
                  value: period_key(option.period),
                  label: option.already_run
                    ? t('pay_run.period_option_already_run', {
                        period: format_month_year(option.period, locale),
                      })
                    : format_month_year(option.period, locale),
                  disabled: option.already_run,
                }))}
                onChange={(next) => {
                  set_period_choice(next);
                  set_start_error(undefined);
                }}
              />
            </FormField>
            <Button
              variant="primary"
              size="sm"
              disabled={is_starting || !chosen_bounds}
              onClick={() => {
                if (!chosen_bounds) return;
                set_is_starting(true);
                set_start_error(undefined);
                // `.finally()` resets `is_starting` regardless of outcome
                // (adversarial review finding 4) -- `start_pay_run` itself
                // never throws (see `use_pay_runs.ts`'s docblock), but
                // resetting in `.then()` alone would leave the button
                // permanently disabled if that contract were ever violated
                // by a future change.
                void start_pay_run(chosen_bounds)
                  .then((result) => {
                    if (result.outcome === 'failure') {
                      set_start_error(result.failure);
                    } else {
                      on_started(
                        result.skipped_foreign_currency_employees ?? [],
                        result.back_pay_warnings ?? [],
                      );
                    }
                  })
                  .finally(() => {
                    set_is_starting(false);
                  });
              }}
            >
              {is_starting
                ? t('pay_run.starting')
                : chosen_period
                  ? t('pay_run.start_pay_run_for', {
                      period: format_month_year(chosen_period, locale),
                    })
                  : t('pay_run.start_pay_run')}
            </Button>
            {start_error === 'step_up_required' ? (
              <div className="mt-2" role="alert">
                <p className="nv-help is-error mt-0">
                  {t('pay_run.start_step_up_required')}
                </p>
                <div className="flex gap-2 mt-2">
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => {
                      restartSignIn();
                    }}
                  >
                    {t('auth.step_up.sign_in_again_cta')}
                  </Button>
                </div>
              </div>
            ) : start_error === 'entitlement_required' ? (
              <p className="nv-help is-error mt-0" role="alert">
                {t('pay_run.entitlement_required_description')}
              </p>
            ) : start_error ? (
              <p className="nv-help is-error mt-0" role="alert">
                {t('pay_run.start_error')}
              </p>
            ) : null}
            {back_link}
          </div>
        ) : (
          // A read-only OAuth grant has a real explanation worth showing
          // (adversarial review finding 3) -- the OTHER reason
          // `start_pay_run` can be `undefined` here (no prior pay run to
          // resolve default GL accounts from, see `use_pay_runs.ts`'s module
          // docblock) has no user-actionable explanation to offer, so it
          // stays silent.
          <div className="flex flex-col items-center gap-2">
            {is_read_only_grant ? (
              <p className="nv-help mt-0">{t('pay_run.read_only_notice')}</p>
            ) : null}
            {back_link}
          </div>
        )
      }
    />
  );
}
